import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'

import { gsap, ScrollTrigger, WF_EASE, registerGsap, useIsomorphicLayoutEffect } from './gsap'
import { setupScrollReveals } from './scroll-reveals'
import { setupScrubbed } from './scrubbed'
import { setupHomeHeroLoad, setupStaggerLoad, setupSignupCards } from './page-load'
import { setupMouseInteractions } from './mouse'
import { setupFaq } from './faq'

/**
 * Runs the full translated Webflow interaction set for whichever page is mounted.
 * Each pattern is selector-driven and no-ops when its target elements are absent,
 * so this single component safely covers every route. Everything lives in one
 * gsap.context scoped to <main> and is reverted on navigation.
 */
export const PageAnimations: FC = (): ReactNode => {
    const router = useRouter()

    useIsomorphicLayoutEffect(() => {
        registerGsap()

        const root = document.querySelector<HTMLElement>('main')
        if (!root) return

        const isHome = Boolean(root.querySelector('.hero-text-wrapper'))

        // The nav pill lives in the shared navbar, OUTSIDE <main>, so it cannot be
        // revealed from the main-scoped context below. Reveal it directly here, and
        // first, so it appears even if a later setup throws. Timing mirrors Webflow's
        // load (later behind the home hero, quicker on inner pages).
        const nav = document.querySelector<HTMLElement>('.nav-menu-container')
        const navTween = nav
            ? gsap.fromTo(
                nav,
                { autoAlpha: 0 },
                { autoAlpha: 1, duration: isHome ? 1 : 0.6, delay: isHome ? 1.5 : 0.3, ease: WF_EASE }
            )
            : null

        const listenerCleanups: Array<() => void> = []

        const ctx = gsap.context(() => {
            setupHomeHeroLoad(root)
            setupStaggerLoad(root)
            setupSignupCards(root)
            setupScrollReveals(root)
            setupScrubbed(root)

            const mouseCleanup = setupMouseInteractions(root)
            if (mouseCleanup) listenerCleanups.push(mouseCleanup)

            const faqCleanup = setupFaq(root)
            if (faqCleanup) listenerCleanups.push(faqCleanup)
        }, root)

        // Recalculate scroll positions once images/fonts have settled.
        const refresh = (): void => ScrollTrigger.refresh()
        const refreshTimer = window.setTimeout(refresh, 300)
        window.addEventListener('load', refresh)

        return () => {
            window.clearTimeout(refreshTimer)
            window.removeEventListener('load', refresh)
            listenerCleanups.forEach((fn) => fn())
            navTween?.kill()
            ctx.revert()
        }
    }, [router.asPath])

    return null
}
